/**
 * Formatação pt-BR. Puramente apresentacional: nenhuma função aqui decide
 * regra de negócio nem calcula métrica (invariante 3 do CLAUDE.md).
 */

const LOCALE = 'pt-BR';

/** A partir daqui número grande pode ser abreviado (DESIGN_SYSTEM seção 7). */
export const ABBREVIATION_THRESHOLD = 10_000;

export type NumberFormat = 'number' | 'percent' | 'currency' | 'compact';

export interface FormatNumberOptions {
  /** Casas decimais fixas. Padrão: 0 para inteiro, 1 para abreviado. */
  decimals?: number;
  /**
   * `"auto"` abrevia só a partir de 10.000 (1,2 mil -> "12,4k").
   * `"never"` sempre por extenso — use em contagem crítica (total de leads
   * do dia, valor financeiro), onde abreviar esconde informação.
   */
  abbreviate?: 'auto' | 'never';
  /** Moeda ISO quando `format === "currency"`. Padrão BRL. */
  currency?: string;
}

function nf(options: Intl.NumberFormatOptions): Intl.NumberFormat {
  return new Intl.NumberFormat(LOCALE, options);
}

/** `1284` -> `"1.284"` · `12400` -> `"12,4k"` · `1_100_000` -> `"1,1M"` */
export function formatNumber(value: number, options: FormatNumberOptions = {}): string {
  const { decimals, abbreviate = 'auto' } = options;
  const abs = Math.abs(value);

  if (abbreviate === 'auto' && abs >= ABBREVIATION_THRESHOLD) {
    const digits = decimals ?? 1;
    if (abs >= 1_000_000_000) {
      return `${nf({ maximumFractionDigits: digits }).format(value / 1_000_000_000)}B`;
    }
    if (abs >= 1_000_000) {
      return `${nf({ maximumFractionDigits: digits }).format(value / 1_000_000)}M`;
    }
    return `${nf({ maximumFractionDigits: digits }).format(value / 1000)}k`;
  }

  return nf({
    minimumFractionDigits: decimals ?? 0,
    maximumFractionDigits: decimals ?? 0,
  }).format(value);
}

/** `0.124` -> `"12,4%"`. Recebe FRAÇÃO (0..1), não 0..100. */
export function formatPercent(fraction: number, decimals = 1): string {
  return nf({
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(fraction);
}

export function formatCurrency(value: number, currency = 'BRL', decimals = 2): string {
  return nf({
    style: 'currency',
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/** `"12 jun"`, `"12 jun 2026"` — sempre no fuso passado pelo chamador. */
export function formatDate(
  value: Date | string | number,
  options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short' },
): string {
  const date = value instanceof Date ? value : new Date(value);
  return new Intl.DateTimeFormat(LOCALE, options).format(date);
}

/** Travessão de "sem dados" — não é hífen, não é zero. */
export const NO_DATA_DASH = '—';
